import { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';

const REPLACE_STATE_DEBOUNCE_TIME = 100;

/**
 * Save and restore container scroll position to history state
 */
export function useScrollRestoration<T extends HTMLElement = HTMLDivElement>(
  scrollRestorationPrefix: string | undefined,
) {
  const ref = useRef<T>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const container = ref.current;
    if (!scrollRestorationPrefix || !container) return;

    // Načteme stav scrollu ze state
    const state = location.state ?? {};

    const savedTop = state?.[`${scrollRestorationPrefix}Top`];
    if (typeof savedTop !== 'undefined') container.scrollTop = savedTop;

    const savedLeft = state?.[`${scrollRestorationPrefix}Left`];
    if (typeof savedLeft !== 'undefined') container.scrollLeft = savedLeft;

    let validPathname: string | undefined = location.pathname;

    const saveScrollPosition = debounce(() => {
      const isLocationMatch = location.pathname === validPathname;
      validPathname = undefined; // místo null
      if (!isLocationMatch) return;

      // Uložíme nový stav scrollu do history
      navigate(location.pathname, {
        replace: true,
        state: {
          ...location.state,
          [`${scrollRestorationPrefix}Top`]: container.scrollTop,
          [`${scrollRestorationPrefix}Left`]: container.scrollLeft,
        },
      });
      validPathname = location.pathname;
    }, REPLACE_STATE_DEBOUNCE_TIME);

    container.addEventListener('scroll', saveScrollPosition);
    return () => container.removeEventListener('scroll', saveScrollPosition);
  }, [navigate, location, scrollRestorationPrefix]);

  return ref;
}
